/**
 * Measure an element's content width in CSS pixels with `ResizeObserver`.
 *
 * The chart uses this instead of letting CSS scale a fixed-size SVG down: the
 * SVG's `viewBox` is chosen from the measured width, so one SVG user unit stays
 * about one CSS pixel and the in-SVG labels keep the pixel size they were
 * designed at on every viewport.
 *
 * `ResizeObserver` is feature-guarded on purpose. Where it does not exist the
 * hook reports {@link UNMEASURED_WIDTH} and the caller falls back to its own
 * default; jsdom has no `ResizeObserver` (and returns a zero rect even when it
 * does measure), so this keeps component tests deterministic.
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import type { RefCallback } from 'react'

/** Width reported before measurement, and wherever measurement is unavailable. */
export const UNMEASURED_WIDTH = 0

export interface ElementWidth<T extends HTMLElement> {
  /** Attach to the element to measure. */
  ref: RefCallback<T>
  /** Measured content width in CSS pixels, or {@link UNMEASURED_WIDTH}. */
  width: number
}

/**
 * Observe an element's content width.
 *
 * The state only moves when the ROUNDED width changes, which is what stops a
 * layout nudge (a height-only resize, a sub-pixel reflow) from re-triggering the
 * observer in a loop. The observer is disconnected on unmount and whenever the
 * measured element changes.
 */
export function useElementWidth<T extends HTMLElement = HTMLDivElement>(): ElementWidth<T> {
  const [width, setWidth] = useState<number>(UNMEASURED_WIDTH)
  const observerRef = useRef<ResizeObserver | null>(null)

  const ref = useCallback<RefCallback<T>>((node) => {
    observerRef.current?.disconnect()
    observerRef.current = null
    if (node === null) return
    if (typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver((entries) => {
      const entry = entries.at(-1)
      if (entry === undefined) return
      const measured = Math.round(entry.contentRect.width)
      setWidth((current) => (current === measured ? current : measured))
    })
    observer.observe(node)
    observerRef.current = observer
  }, [])

  useEffect(() => () => observerRef.current?.disconnect(), [])

  return { ref, width }
}
